/**
 * RetentionScreen — Unsubscribe flow with exit survey
 *
 * Shown when a user initiates cancellation from SubscriptionDrawer.
 * Collects structured feedback (radio + free text) before redirecting
 * to the platform-specific cancel link (App Store / Play Store).
 *
 * Flow:
 *   1. User taps "Cancel Subscription" in SubscriptionDrawer
 *   2. RetentionScreen appears (modal / full-screen)
 *   3. User sees empathetic header + exit survey
 *   4. User selects reason, optionally types details
 *   5. "Continue to Cancel" → POST feedback → Open platform cancel link
 *   6. OR "Never mind, I'll stay" → Dismiss
 *
 * Backend endpoint (future): POST /api/subscription/exit-survey
 * For now, feedback is logged to console and stored locally.
 *
 * Mission 33 — Subscription System
 * Created: February 2026
 */

import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ScrollView,
  Platform,
  Linking,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { CoachCedarColors, Spacing } from '@/utils/styleConverter';
import type { SubscriptionTier } from '@/services/subscriptionService';
import { secureTokenStorage } from '@/services/secureTokenStorage';
import { getApiBaseUrl } from '@/utils/apiConfig';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RetentionScreenProps {
  /** Current subscription tier */
  currentTier: SubscriptionTier;
  /** Callback when user decides to stay */
  onStay: () => void;
  /** Callback after survey submitted and cancel link opened */
  onCancelComplete: () => void;
  /** Optional: current renewal date for display */
  renewalDate?: string | null;
}

interface ExitReason {
  id: string;
  label: string;
  emoji: string;
}

// ---------------------------------------------------------------------------
// Exit survey options
// ---------------------------------------------------------------------------

const EXIT_REASONS: ExitReason[] = [
  {
    id: 'too_expensive',
    label: "It's more than I want to spend right now",
    emoji: '💰',
  },
  {
    id: 'not_using_enough',
    label: "I'm not using it as much as I expected",
    emoji: '📉',
  },
  {
    id: 'features_missing',
    label: "It doesn't have the features I need",
    emoji: '🔧',
  },
  {
    id: 'found_alternative',
    label: 'I found something else that works better for me',
    emoji: '🔄',
  },
  {
    id: 'other',
    label: 'Something else',
    emoji: '💬',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RetentionScreen({
  currentTier,
  onStay,
  onCancelComplete,
  renewalDate,
}: RetentionScreenProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textInputRef = useRef<TextInput>(null);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const tierDisplayName =
    currentTier === 'extended' ? 'CC Pro' : currentTier === 'full' ? 'CC Chat+' : currentTier === 'basic' ? 'CC Essentials' : 'Coach Cedar';

  const formattedRenewalDate = renewalDate
    ? new Date(renewalDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  /** Open the platform-specific subscription management / cancel page */
  const openPlatformCancel = () => {
    if (Platform.OS === 'ios') {
      Linking.openURL('https://apps.apple.com/account/subscriptions');
    } else if (Platform.OS === 'android') {
      Linking.openURL(
        'https://play.google.com/store/account/subscriptions',
      );
    } else {
      // Web fallback
      if (typeof window !== 'undefined') {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          window.open(
            'https://apps.apple.com/account/subscriptions',
            '_blank',
          );
        } else {
          window.open(
            'https://play.google.com/store/account/subscriptions',
            '_blank',
          );
        }
      }
    }
  };

  /** Submit survey feedback and redirect to cancel */
  const handleSubmitAndCancel = async () => {
    if (!selectedReason) return;

    setIsSubmitting(true);

    try {
      // Build payload
      const payload = {
        reason_id: selectedReason,
        reason_label:
          EXIT_REASONS.find((r) => r.id === selectedReason)?.label ?? '',
        free_text: freeText.trim() || null,
        tier: currentTier,
        submitted_at: new Date().toISOString(),
        platform: Platform.OS,
      };

      // Attempt to send to backend (graceful failure — don't block cancel)
      try {
        // Bug #5 fix: Use secureTokenStorage instead of direct localStorage access.
        // localStorage.getItem('token') crashes on native and violates platform abstraction.
        // secureTokenStorage.getCedarJwt() is already platform-aware internally.
        const token = await secureTokenStorage.getCedarJwt();

        if (token) {
          const baseUrl = Platform.OS === 'web' ? '' : getApiBaseUrl();
          await fetch(`${baseUrl}/api/subscription/exit-survey`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });
        }
      } catch {
        // Non-critical — log locally as fallback
        console.log('📊 Exit survey (offline fallback):', payload);
      }

      // Open platform cancel link
      openPlatformCancel();

      // Notify parent
      onCancelComplete();
    } catch (error) {
      console.error('Exit survey submission error:', error);
      // Still open the cancel link even if survey fails
      openPlatformCancel();
      onCancelComplete();
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Select a reason (tap toggles off if already selected) */
  const handleSelectReason = (reasonId: string) => {
    setSelectedReason((prev) => (prev === reasonId ? null : reasonId));

    // Auto-focus free text when "other" is selected
    if (reasonId === 'other') {
      setTimeout(() => textInputRef.current?.focus(), 200);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
    >
      {/* ── Empathetic header ───────────────────────────────── */}

      <View style={styles.header}>
        <Text style={styles.headerEmoji}>🌲</Text>
        <Text style={styles.headerTitle}>
          We're sorry to see you go
        </Text>
        <Text style={styles.headerSubtitle}>
          Before you cancel your {tierDisplayName} subscription, we'd
          love to understand why — it helps us improve.
        </Text>
        {formattedRenewalDate && (
          <Text style={styles.accessNote}>
            You'll keep full access until {formattedRenewalDate}.
          </Text>
        )}
      </View>

      {/* ── Exit survey ─────────────────────────────────────── */}

      <View style={styles.surveySection}>
        <Text style={styles.surveyLabel}>
          What's the main reason you're leaving?
        </Text>

        {EXIT_REASONS.map((reason) => {
          const isSelected = selectedReason === reason.id;

          return (
            <TouchableOpacity
              key={reason.id}
              style={[
                styles.reasonCard,
                isSelected && styles.reasonCardSelected,
              ]}
              onPress={() => handleSelectReason(reason.id)}
              activeOpacity={0.7}
              accessibilityRole="radio"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={reason.label}
            >
              <View style={styles.radioOuter}>
                {isSelected && <View style={styles.radioInner} />}
              </View>
              <Text style={styles.reasonEmoji}>{reason.emoji}</Text>
              <Text
                style={[
                  styles.reasonText,
                  isSelected && styles.reasonTextSelected,
                ]}
              >
                {reason.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* ── Free text ───────────────────────────────────────── */}

      <View style={styles.freeTextSection}>
        <Text style={styles.freeTextLabel}>
          Anything else you'd like to share?{' '}
          <Text style={styles.optionalTag}>(optional)</Text>
        </Text>
        <TextInput
          ref={textInputRef}
          style={styles.freeTextInput}
          value={freeText}
          onChangeText={setFreeText}
          placeholder="Your feedback helps us build a better Coach Cedar..."
          placeholderTextColor="#AAA"
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          maxLength={500}
          accessibilityLabel="Additional feedback"
        />
        {freeText.length > 0 && (
          <Text style={styles.charCount}>
            {freeText.length}/500
          </Text>
        )}
      </View>

      {/* ── Action buttons ──────────────────────────────────── */}

      <View style={styles.actionsSection}>
        {/* Primary: Stay */}
        <TouchableOpacity
          style={styles.stayButton}
          onPress={onStay}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel="Keep my subscription"
        >
          <Text style={styles.stayButtonText}>
            Never mind, I'll stay
          </Text>
        </TouchableOpacity>

        {/* Secondary: Continue to cancel */}
        <TouchableOpacity
          style={[
            styles.cancelButton,
            !selectedReason && styles.cancelButtonDisabled,
          ]}
          onPress={handleSubmitAndCancel}
          disabled={!selectedReason || isSubmitting}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Continue to cancel subscription"
          accessibilityState={{
            disabled: !selectedReason || isSubmitting,
          }}
        >
          {isSubmitting ? (
            <ActivityIndicator size="small" color="#999" />
          ) : (
            <Text
              style={[
                styles.cancelButtonText,
                !selectedReason && styles.cancelButtonTextDisabled,
              ]}
            >
              Continue to Cancel
            </Text>
          )}
        </TouchableOpacity>

        <Text style={styles.cancelFinePrint}>
          Cancellation is managed by{' '}
          {Platform.OS === 'ios'
            ? 'the App Store'
            : Platform.OS === 'android'
              ? 'Google Play'
              : 'your app store'}
          . You won't be charged again after your current period ends.
        </Text>
      </View>
    </ScrollView>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const CARD_BG = '#F8F9FA';
const SELECTED_BG = '#EDF5F0';
const SELECTED_BORDER = CoachCedarColors.cedarPrime ?? '#1F6C4F';
const RADIO_COLOR = CoachCedarColors.cedarPrime ?? '#1F6C4F';

const styles = StyleSheet.create({
  // Layout
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  contentContainer: {
    padding: Spacing.lg ?? 20,
    paddingBottom: 40,
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: 28,
  },
  headerEmoji: {
    fontSize: 40,
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#113C2B',
    textAlign: 'center',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 15,
    color: '#555',
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: 8,
  },
  accessNote: {
    fontSize: 13,
    color: RADIO_COLOR,
    fontWeight: '500',
    textAlign: 'center',
    marginTop: 10,
  },

  // Survey
  surveySection: {
    marginBottom: 20,
  },
  surveyLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#222',
    marginBottom: 14,
  },
  reasonCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CARD_BG,
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1.5,
    borderColor: 'transparent',
  },
  reasonCardSelected: {
    backgroundColor: SELECTED_BG,
    borderColor: SELECTED_BORDER,
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: '#CCC',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: RADIO_COLOR,
  },
  reasonEmoji: {
    fontSize: 18,
    marginRight: 10,
  },
  reasonText: {
    flex: 1,
    fontSize: 14,
    color: '#444',
    lineHeight: 20,
  },
  reasonTextSelected: {
    color: '#113C2B',
    fontWeight: '500',
  },

  // Free text
  freeTextSection: {
    marginBottom: 28,
  },
  freeTextLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#222',
    marginBottom: 8,
  },
  optionalTag: {
    fontWeight: '400',
    color: '#999',
    fontSize: 13,
  },
  freeTextInput: {
    backgroundColor: CARD_BG,
    borderRadius: 10,
    padding: 14,
    fontSize: 14,
    color: '#333',
    minHeight: 100,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    lineHeight: 20,
    ...(Platform.OS === 'web'
      ? ({ outlineColor: SELECTED_BORDER } as any)
      : {}),
  },
  charCount: {
    fontSize: 12,
    color: '#AAA',
    textAlign: 'right',
    marginTop: 4,
  },

  // Actions
  actionsSection: {
    alignItems: 'center',
  },
  stayButton: {
    backgroundColor: RADIO_COLOR,
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 32,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  stayButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 32,
    width: '100%',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#CCC',
    marginBottom: 14,
  },
  cancelButtonDisabled: {
    opacity: 0.45,
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 15,
    fontWeight: '500',
  },
  cancelButtonTextDisabled: {
    color: '#AAA',
  },
  cancelFinePrint: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    lineHeight: 18,
    paddingHorizontal: 12,
  },
});
